import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FEATURES, FeatureKey } from '../config/featureFlags';

/**
 * BottomSidebar Component
 * 
 * A mobile-style bottom navigation bar component that provides navigation for the dashboard.
 * Similar to mobile app bottom navigation with icons and labels.
 * 
 * Props:
 * - currentPath: The current active route path
 * 
 * Usage:
 * <BottomSidebar currentPath="/devices" />
 * 
 * Features:
 * - Mobile-style bottom navigation
 * - Home, Reports, Alerts, Share, Profile navigation
 * - Active state highlighting
 * - Responsive design
 */
interface BottomSidebarProps {
  currentPath: string;
}

const BottomSidebar: React.FC<BottomSidebarProps> = ({ currentPath }) => {
  const navigate = useNavigate();
  const theme = document.documentElement.getAttribute('data-theme') as 'light' | 'dark' ?? 'light';

  const navigationItems = [
    { 
      activeIcon: `${process.env.PUBLIC_URL}/BottombarIcons/HomeActiveIcon.svg`, 
      inactiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/HomeInActiveIcon.svg`, 
      darkThemeInActiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/HomeDActiveIcon.svg`,
      label: 'Home', 
      path: '/devices',
      allowedPaths: ['/devices', '/manually-add-device', '/add-device', '/edit-device'] 
    },
    { 
      activeIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ReportsActiveIcon.svg`, 
      inactiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ReportsInActiveIcon.svg`, 
      darkThemeInActiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ReportsDActiveIcon.svg`,
      label: 'Reports', 
      path: '/reports',
      allowedPaths: ['/reports'] 
    },
    { 
      activeIcon: `${process.env.PUBLIC_URL}/BottombarIcons/AlertsActiveIcon.svg`, 
      inactiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/AlertsInActiveIcon.svg`, 
      darkThemeInActiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/AlertsDActiveIcon.svg`,
      label: 'Alerts', 
      path: '/alerts',
      allowedPaths: ['/alerts'] 
    },
    { 
      activeIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ShareActiveIcon.svg`, 
      inactiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ShareInActiveIcon.svg`, 
      darkThemeInActiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ShareDActiveIcon.svg`,
      label: 'Share', 
      path: '/share',
      allowedPaths: ['/share'] 
    },
    {
      activeIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ProfileActiveIcon.svg`,
      inactiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ProfileInActiveIcon.svg`,
      darkThemeInActiveIcon: `${process.env.PUBLIC_URL}/BottombarIcons/ProfileDActiveIcon.svg`,
      label: 'Profile',
      path: '/settings',
      allowedPaths: ['/settings']
    },
    {
      activeIcon: null,
      inactiveIcon: null,
      darkThemeInActiveIcon: null,
      label: 'New Design',
      path: '/new-device-design',
      allowedPaths: ['/new-device-design'],
      isNewDesign: true,
      feature: 'newDesign' as FeatureKey,
    },
  ].filter((item: any) => !item.feature || FEATURES[item.feature as FeatureKey]);

  return (
    <div className="fixed w-full bottom-0 px-4 py-2 z-50 bg-[var(--modal-background)] bottombar-container">
      <div className="flex justify-between items-center px-2">
        {navigationItems.map((item: any) => {
          const isActive = item.allowedPaths.some((path: string) =>
            currentPath === path || currentPath.startsWith(path + '/')
          );

          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex items-center justify-center gap-1.5 py-2 px-3 rounded-[0.625rem] transition-colors ${
                isActive
                  ? 'bg-gradient-to-r from-[var(--gradient-button-left-color)] to-[var(--gradient-button-right-color)]'
                  : ' '
              }`}
            >
              {item.isNewDesign ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                  className={`w-6 h-6 ${isActive ? 'text-white' : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
                </svg>
              ) : (
                <img
                  src={isActive ? item.activeIcon : theme === 'light' ? item.inactiveIcon : item.darkThemeInActiveIcon}
                  alt={item.label}
                />
              )}
              {isActive && (
                <span className="text-sm font-medium text-white">{item.label}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default BottomSidebar;
